import axios from 'axios';
import config from '../config/default';
import { getCookie, setCookie } from '../utils';

/**
 * Get cookie for character cloud submission
 * @param {String} eventId - id of the event
 */
export const getCharacterCloudCookie = eventId => getCookie(`${eventId}_character_cloud_in`);

/**
 * Set cookie for character cloud submission
 * @param {String} eventId - id of the event
 */
export const setCharacterCloudCookie = eventId => setCookie(`${eventId}_character_cloud_in`, true, 3650);

/**
 * Get available adjectives for autocomplete
 * @param {String} organizationId - id of the organization
 * @param {String} eventId - id of the event
 * @param {String} query - search prefix (optional)
 * @return {Promise<{message: string, status: number}|{message: string, status: number, data: object}>}
 */
export const getAdjectives = async (organizationId, eventId, query = '') => {
    try {
        const url = `${config.viewlogiesApiUrl}/client/event/${organizationId}/${eventId}/character-cloud/adjectives${query ? `?q=${encodeURIComponent(query)}` : ''}`;
        const { status, data } = await axios({
            method: 'get',
            url,
            headers: { Accept: 'application/json' },
            withCredentials: true,
        });
        return { status, data, message: 'Success!' };
    } catch (e) {
        // If endpoint doesn't exist yet (404), return empty array
        if (e.response && e.response.status === 404) {
            return { status: 404, data: { adjectives: [] }, message: 'Endpoint not found' };
        }
        console.error(e);
        const { status, data } = e.response || {};
        return { status: status || 500, message: data?.message || e.message, data: { adjectives: [] } };
    }
};

/**
 * Submit adjectives to character cloud
 * @param {String} organizationId - id of the organization
 * @param {String} eventId - id of the event
 * @param {String[]} adjectives - array of adjectives
 * @param {String} recaptchaToken - token from recaptcha for backend verification
 * @param {String} submissionEmail - email address of the submitter (required)
 * @return {Promise<{message: string, status: number}|{message: *, status: number}>}
 */
export const submitAdjectives = async (organizationId, eventId, adjectives, recaptchaToken, submissionEmail) => {
    try {
        const url = `${config.viewlogiesApiUrl}/client/event/${organizationId}/${eventId}/character-cloud`;
        const { status } = await axios({
            method: 'post',
            url,
            data: { adjectives, recaptchaToken, submissionEmail: submissionEmail.trim() },
            headers: { Accept: 'application/json' },
            withCredentials: true,
        });
        return { status, message: 'Success!' };
    } catch (e) {
        console.error(e);
        const { status, data } = e.response || {};
        return { status: status || 500, message: data?.message || e.message };
    }
};

/**
 * Get character cloud data (aggregated word counts)
 * @param {String} organizationId - id of the organization
 * @param {String} eventId - id of the event
 * @return {Promise<{message: string, status: number}|{message: string, status: number, data: object}>}
 */
export const getCharacterCloud = async (organizationId, eventId) => {
    try {
        const url = `${config.viewlogiesApiUrl}/client/event/${organizationId}/${eventId}/character-cloud`;
        const { status, data } = await axios({
            method: 'get',
            url,
            headers: { Accept: 'application/json' },
            withCredentials: true,
        });
        return { status, data, message: 'Success!' };
    } catch (e) {
        // If endpoint doesn't exist yet (404), return empty data instead of error
        if (e.response && e.response.status === 404) {
            return { status: 404, data: { words: [] }, message: 'Endpoint not found' };
        }
        console.error(e);
        const { status, data } = e.response || {};
        return { status: status || 500, message: data?.message || e.message, data: { words: [] } };
    }
};
